<template>
  <section class="customer-detail-retainers">
    <article class="customer-detail-card customer-detail-card--accent-primary">
      <SalesFlatRatesToolbar
        :show-customer-filter="false"
        :initial-date-preset-key="''"
        @search-change="handleToolbarSearch"
        @filters-change="handleToolbarFilters"
        @apply="applyToolbarFilters"
      />

      <StatusTabs
        :tabs="statusTabs"
        :active-tab="activeStatusTab"
        @select="selectStatusTab"
      />

      <SalesFlatRatesTable
        v-if="isOpenStatusTab"
        :rows="paginatedRows"
        :loading="effectiveLoading"
        :pagination="tablePagination"
        :empty-text="text.table.empty"
        :sort-field="sortField"
        :sort-direction="sortDirection"
        @invoice-sale="handleInvoiceSale"
        @simple-note-sale="handleSimpleNoteSale"
        @pay-sale="handlePaySale"
        @change-sale-status="handleBusinessChangeSaleStatus"
        @edit-sale="handleEditSale"
        @view-sale="handleViewSale"
        @delete-sale="handleDeleteSale"
        @page-change="handlePageChange"
        @sort-change="handleSortChange"
      />

      <SharedSalesTable
        v-else
        :rows="paginatedRows"
        :loading="effectiveLoading"
        :pagination="tablePagination"
        :empty-text="businessTableEmptyText"
        :show-customer-column="false"
        :sort-field="businessSortField"
        :sort-direction="businessSortDirection"
        @change-sale-status="handleBusinessChangeSaleStatus"
        @edit-sale="handleBusinessEditSale"
        @delete-sale="handleBusinessDeleteSale"
        @page-change="handlePageChange"
        @sort-change="handleBusinessSortChange"
      />

      <SaleModal
        :show="showCreateFlatRateModal"
        :mode="saleModalMode"
        :initial-data="saleModalInitialData"
        sale-kind="business"
        service-filter-mode="flat-rate"
        :submitting="createFlatRateSubmitting"
        :advertising-enabled="isAdvertisingModuleActive"
        @close="closeCreateFlatRateModal"
        @delete="handleDeleteSaleFromModal"
        @submit="handleCreateFlatRate"
      />

      <PaymentPlanModal
        :show="isPaymentPlanModalOpen"
        mode="edit"
        :initial-data="editingPaymentPlan"
        :sale-id="editingPaymentSaleId"
        :sale-price="editingPaymentSalePrice"
        :existing-planned-total="editingExistingPlannedTotal"
        :submitting="isPaymentPlanSubmitting"
        @close="closePaymentPlanModal"
        @submit="submitPaymentPlanEdit"
      />

      <ConfirmActionModal
        :show="isDeleteConfirmModalOpen"
        :title="deleteConfirmTitle"
        :subtitle="deleteConfirmSubtitle"
        :message="deleteConfirmMessage"
        :confirm-text="deleteConfirmButtonText"
        @cancel="closeDeleteConfirmModal"
        @confirm="confirmDeleteSale"
      />

      <SaleStatusModal
        :show="isStatusModalOpen"
        :sale-name="resolveSelectedSaleName()"
        :current-status-label="resolveSelectedSaleStatusLabel()"
        :statuses="statusOptions"
        :selected-status-id="resolveSelectedSaleStatusId()"
        :submitting="statusModalSubmitting"
        @close="closeStatusModal"
        @submit="confirmUpdateSaleStatus"
      />
    </article>
  </section>
</template>

<script>
import SaleModal from '@/components/sales/SaleModal.vue';
import PaymentPlanModal from '@/components/sales/shared/paymentPlan/PaymentPlanModal.vue';
import ConfirmActionModal from '@/components/global/ConfirmActionModal.vue';
import SaleStatusModal from '@/components/sales/shared/SaleStatusModal.vue';
import SalesFlatRatesToolbar from '@/components/salesFlatRates/SalesFlatRatesToolbar.vue';
import SalesFlatRatesTable from '@/components/salesFlatRates/SalesFlatRatesTable.vue';
import SharedSalesTable from '@/components/sales/shared/SalesTable.vue';
import StatusTabs from '@/components/common/StatusTabs.vue';
import { createDefaultSalesToolbarFilters } from '@/composables/sales/useSalesFilters';
import { useDomainText } from '@/composables/text/useDomainText';
import {
  createSalesFlatRatesPageState,
  salesFlatRatesPageComputed,
  salesFlatRatesPageMethods,
} from '@/composables/salesFlatRates/useSalesFlatRatesPage';

const { catalog: text } = useDomainText('flatRates');

export default {
  name: 'CustomerDetailRetainersTab',
  components: {
    SaleModal,
    PaymentPlanModal,
    ConfirmActionModal,
    SaleStatusModal,
    SalesFlatRatesToolbar,
    SalesFlatRatesTable,
    SharedSalesTable,
    StatusTabs,
  },
  props: {
    customer: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['view-sale-detail', 'change-sale-status', 'update-count'],
  data() {
    const baseState = createSalesFlatRatesPageState(text);
    const defaultFilters = createDefaultSalesToolbarFilters();

    return {
      text,
      ...baseState,
      headerActions: [],
      toolbarFilters: defaultFilters,
      appliedToolbarFilters: defaultFilters,
    };
  },
  computed: {
    customerId() {
      return Number(this.customer?.id || 0);
    },
    isAdvertisingModuleActive() {
      const can = typeof this.$can === 'function' ? this.$can : null;
      if (!can) {
        return true;
      }

      return can('sales.advertising');
    },
    ...salesFlatRatesPageComputed,
    effectiveLoading() {
      return this.loading || this.tableLoading;
    },
    isPaymentPlanDeleteTarget() {
      return String(this.deletingTarget || '').trim().toLowerCase() === 'payment-plan';
    },
    deleteConfirmTitle() {
      return this.isPaymentPlanDeleteTarget
        ? this.text.confirms.deletePaymentPlan.title
        : this.text.confirms.delete.title;
    },
    deleteConfirmSubtitle() {
      return this.isPaymentPlanDeleteTarget
        ? this.text.confirms.deletePaymentPlan.subtitle
        : this.text.confirms.delete.subtitle;
    },
    deleteConfirmButtonText() {
      return this.isPaymentPlanDeleteTarget
        ? this.text.confirms.deletePaymentPlan.confirm
        : this.text.confirms.delete.confirm;
    },
    deleteConfirmMessage() {
      if (this.isPaymentPlanDeleteTarget) {
        const planName = String(this.deletingSale?.name || this.deletingSale?.sale_name || '').trim()
          || this.text.confirms.deletePaymentPlan.fallbackName;
        return this.text.confirms.deletePaymentPlan.message(planName);
      }

      const saleName = String(this.deletingSale?.sale_name || this.deletingSale?.name || '').trim() || this.text.confirms.delete.fallbackName;
      return this.text.confirms.delete.message(saleName);
    },
  },
  mounted() {
    this.refreshRowsByFilters();
  },
  watch: {
    customerId(nextId, previousId) {
      if (nextId === previousId) {
        return;
      }

      this.currentPage = 1;
      this.refreshRowsByFilters();
    },
  },
  methods: {
    ...salesFlatRatesPageMethods,
    async fetchMetrics() {
      // Metrics are not shown in customer detail tab.
    },
    buildRemoteListParams() {
      const params = salesFlatRatesPageMethods.buildRemoteListParams.call(this);
      const customerId = Number(this.customerId || 0);

      return {
        ...params,
        customer: customerId > 0 ? [String(customerId)] : [],
      };
    },
    resolveUniqueRetainerBusinessesCount() {
      const source = [
        ...(Array.isArray(this.rowsAll) ? this.rowsAll : []).filter((row) => this.resolveBusinessStatusCategory(row) === 'open'),
        ...this.businessRowsByCategory('closed'),
        ...this.businessRowsByCategory('canceled'),
      ];
      const uniqueSaleIds = new Set();

      source.forEach((row) => {
        const saleId = Number(row?.sale_id || row?.id || 0);
        if (saleId > 0) {
          uniqueSaleIds.add(saleId);
        }
      });

      return uniqueSaleIds.size;
    },
    emitCountUpdate(count = this.resolveUniqueRetainerBusinessesCount()) {
      this.$emit('update-count', {
        key: 'retainers',
        count: Number(count || 0),
      });
    },
    async fetchOpenRows(options = {}) {
      await salesFlatRatesPageMethods.fetchOpenRows.call(this, options);
      this.emitCountUpdate();
    },
    async fetchBusinessRows(options = {}) {
      await salesFlatRatesPageMethods.fetchBusinessRows.call(this, options);
      this.emitCountUpdate();
    },
    handleViewSale(row) {
      const saleId = Number(row?.sale_id || row?.id || 0);
      if (saleId <= 0) {
        return;
      }

      this.$emit('view-sale-detail', { ...row, id: saleId });
    },
  },
};
</script>

<style scoped>
.customer-detail-retainers {
  display: grid;
  gap: 12px;
}
</style>
